import { createFileRoute } from '@tanstack/react-router';
import { useState, useContext } from 'react';
import { AuthContext } from '@/context/AuthContext';
import { useSubscriptionQuery, useInvalidateSubscriptionCache } from '@/hooks/useSubscriptionQuery';

export const Route = createFileRoute('/_protected/subscription')({
  component: SubscriptionPage,
});

function SubscriptionPage() {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const { data: subscription, isLoading: loading } = useSubscriptionQuery(user?.id);
  const invalidateSubscription = useInvalidateSubscriptionCache();
  const [upgrading, setUpgrading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [managingSubscription, setManagingSubscription] = useState(false);

  const handleBackClick = () => {
    history.back();
  };

  const handleUpgrade = async (cycle: 'monthly' | 'annual' = billingCycle) => {
    if (!user?.id) return;

    setUpgrading(true);
    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          billing_cycle: cycle,
          success_url: `${window.location.origin}/subscription?success=true`,
          cancel_url: `${window.location.origin}/subscription?canceled=true`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { checkout_url } = await response.json() as { checkout_url: string };

      // Invalidate subscription cache before redirecting
      if (user?.id) {
        invalidateSubscription(user.id);
      }

      window.location.href = checkout_url;
    } catch (error) {
      console.error('Failed to start upgrade:', error);
      alert('Failed to start upgrade process. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.id || !subscription?.stripe_customer_id) {
      alert('Unable to access subscription management. Please contact support@stack.je for assistance.');
      return;
    }

    setManagingSubscription(true);
    try {
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          return_url: `${window.location.origin}/subscription`,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json() as { portal_url?: string; error?: string };

      if (!data.portal_url) {
        throw new Error('No portal URL received');
      }

      // Invalidate subscription cache before redirecting
      if (user?.id) {
        invalidateSubscription(user.id);
      }

      // Redirect to Stripe Customer Portal
      window.location.href = data.portal_url;
    } catch (error) {
      console.error('Failed to open subscription management:', error);
      alert('Unable to open subscription management. Please contact support@stack.je for assistance.');
    } finally {
      setManagingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-orange-700 p-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto"></div>
            <p className="text-white mt-4">Loading subscription...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentPlan = subscription?.plan_type || 'free';
  const isActive = currentPlan === 'free' || subscription?.status === 'active';

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-orange-700 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Subscription</h1>
            <p className="text-orange-200 mt-1">Manage your Stack.je subscription</p>
          </div>
          <button
            onClick={handleBackClick}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Back
          </button>
        </div>

        {/* Current Plan Status */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white mb-2">Current Plan</h2>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${currentPlan === 'pro'
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                  : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                  }`}>
                  {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)} Plan
                </span>
                <span className={`px-2 py-1 text-xs rounded ${isActive
                  ? 'bg-green-500/20 text-green-300'
                  : 'bg-yellow-500/20 text-yellow-300'
                  }`}>
                  {subscription?.status || 'active'}
                </span>
              </div>
              {subscription?.expires_at && (
                <p className="text-orange-200 text-sm mt-2">
                  {currentPlan === 'pro' ? 'Renews' : 'Expires'} on {new Date(subscription.expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
            {currentPlan === 'pro' && isActive && subscription?.stripe_customer_id && (
              <div>
                <button
                  onClick={handleManageSubscription}
                  disabled={managingSubscription}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {managingSubscription ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Opening...
                    </div>
                  ) : (
                    'Manage Billing'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Plan Comparison */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Free Plan */}
          <div className={`bg-white/10 backdrop-blur-md rounded-xl p-6 border-2 ${currentPlan === 'free' ? 'border-orange-400' : 'border-transparent'
            }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Free Plan</h3>
              {currentPlan === 'free' && (
                <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-sm">
                  Current
                </span>
              )}
            </div>
            <div className="mb-6">
              <span className="text-3xl font-bold text-white">$0</span>
              <span className="text-orange-200 ml-1">/month</span>
            </div>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Create and publish a stack
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Easy to use card builders
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Mobile-optimized sites
              </li>
              <li className="flex items-center text-red-300">
                <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Stack.je footer branding
              </li>
            </ul>
          </div>

          {/* Pro Plan */}
          <div className={`bg-white/10 backdrop-blur-md rounded-xl p-6 border-2 relative ${currentPlan === 'pro' ? 'border-orange-400' : 'border-orange-400'
            }`}>
            {currentPlan !== 'pro' && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-orange-400 to-red-400 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Recommended
                </span>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">Pro Plan</h3>
              {currentPlan === 'pro' && (
                <span className="bg-orange-500/20 text-orange-300 px-3 py-1 rounded-full text-sm">
                  Current
                </span>
              )}
            </div>

            {/* Billing Toggle */}
            {currentPlan !== 'pro' && (
              <div className="mb-6">
                <div className="flex items-stretch justify-center p-1 bg-white/5 rounded-lg">
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 min-h-[3.25rem] flex flex-col justify-center ${billingCycle === 'annual'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-orange-200 hover:text-white'
                      }`}
                  >
                    Annual
                    <span className="block text-xs text-orange-300 mt-0.5">Save 17%</span>
                  </button>
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all duration-200 min-h-[3.25rem] flex flex-col justify-center ${billingCycle === 'monthly'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'text-orange-200 hover:text-white'
                      }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>
            )}

            {/* Pricing Display */}
            <div className="mb-6">
              {billingCycle === 'annual' ? (
                <div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-white">$9.90</span>
                    <span className="text-orange-200 ml-1">/month</span>
                  </div>
                  <div className="text-sm text-orange-300 mt-1">
                    Billed annually ($118.80/year)
                  </div>
                </div>
              ) : (
                <div>
                  <span className="text-3xl font-bold text-white">$11.90</span>
                  <span className="text-orange-200 ml-1">/month</span>
                </div>
              )}
            </div>

            <ul className="space-y-3 mb-6">
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Everything in Free
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Remove Stack.je branding
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Priority support
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Custom domain
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Multiple sites (coming soon)
              </li>
              <li className="flex items-center text-orange-200">
                <svg className="w-5 h-5 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Advanced analytics (coming soon)
              </li>
            </ul>

            {currentPlan === 'pro' ? (
              <button
                onClick={handleManageSubscription}
                disabled={managingSubscription}
                className="w-full bg-gradient-to-r from-orange-600 to-red-700 hover:from-orange-700 hover:to-red-800 text-white py-3 px-6 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {managingSubscription ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Opening...
                  </div>
                ) : (
                  'Manage Subscription'
                )}
              </button>
            ) : (
              <button
                onClick={() => handleUpgrade()}
                disabled={upgrading}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white py-3 px-6 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {upgrading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Redirecting...
                  </div>
                ) : (
                  `Upgrade to Pro (${billingCycle === 'annual' ? '$9.90/mo' : '$11.90/mo'})`
                )}
              </button>
            )}
          </div>
        </div>

        {/* Success/Cancel Messages */}
        {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('success') && (
          <div className="mt-6 bg-green-500/20 border border-green-500/30 text-green-300 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Successfully upgraded to Pro! Your subscription is now active.
            </div>
          </div>
        )}

        {typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('canceled') && (
          <div className="mt-6 bg-yellow-500/20 border border-yellow-500/30 text-yellow-300 px-4 py-3 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.734 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Upgrade was canceled. You can try again anytime.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
